import { CloseOutlined } from '@ant-design/icons';
import { default as Collapse } from 'antd/es/collapse';
import type { CollapseProps } from 'antd/es/collapse';
import { default as Skeleton } from 'antd/es/skeleton';
import { useMemo } from 'react';

import { useGetDisbursementPayoutBatchesBatchIdPayoutDraftsDraftId } from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';

import { TertiaryButton } from '@atoms/buttons/TertiaryButton';

import { getCollapseItemsFromBatchDraft } from './collapseItems';

export type BATCH_PAYOUT_DRAFTS_KNOWN_SECTIONS =
  | 'payout-request'
  | 'sender'
  | 'receiver-bank'
  | 'receiver'
  | 'attachments';

type CollapseItemType = NonNullable<CollapseProps['items']>[number];

export interface CollapseItemWithTypedSection
  extends Omit<CollapseItemType, 'key'> {
  key: BATCH_PAYOUT_DRAFTS_KNOWN_SECTIONS;
}

type Props = {
  onClose: VoidFunction;
  payoutId: string;
  batchId: string;
};

export const BatchPayoutDraftDetails = ({
  onClose,
  payoutId,
  batchId
}: Props) => {
  const organisationId = useOrganisationId();
  const { findAccountByAccountNumber } = useVirtualAccounts();

  const {
    data: { data: payoutData } = {},
    isLoading,
    isError,
    isSuccess
  } = useGetDisbursementPayoutBatchesBatchIdPayoutDraftsDraftId(
    Number(organisationId),
    Number(batchId),
    Number(payoutId),
    {
      query: {
        enabled: Boolean(organisationId && batchId && payoutId)
      }
    }
  );

  const items: CollapseProps['items'] = useMemo(() => {
    if (payoutData && isSuccess) {
      return getCollapseItemsFromBatchDraft(
        payoutData,
        Number(payoutId),
        findAccountByAccountNumber
      );
    }
    return [];
  }, [payoutData, isSuccess, payoutId, findAccountByAccountNumber]);

  return (
    <div
      style={{ flex: 0.25, borderRadius: '.75rem' }}
      className="cool-shadow overflow-y-auto"
    >
      <div className="flex justify-between rounded-t-xl bg-white px-6 py-3.5 text-xl">
        Details
        <TertiaryButton icon={<CloseOutlined />} onClick={onClose} />
      </div>
      <div className="overflow-auto">
        {isLoading && <Skeleton />}
        {isError && (
          <div className="flex h-full items-center justify-center">
            Something went wrong
          </div>
        )}
        <Collapse
          className="antd-details-collapse bg-white pb-0"
          expandIconPosition="end"
          accordion
          bordered={false}
          defaultActiveKey="payout-request"
          items={items}
        />
      </div>
    </div>
  );
};
